import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  AXIOS_INSTANCE,
  fetchUserRoleReason,
  grantRoleToUser,
  useFetchGrantedRolesToUser
} from '@jobandtalent/auth-api-client-v2';
import { FetchRoles200, Role } from '@jobandtalent/auth-api-client-v2/dist/apiClient/model';
import { Select, Textarea } from '@jobandtalent/design-system';

import useSWRInfinite from 'swr/infinite';

import { formatDataScopesData, formatDataScopesInput } from 'src/common/dataScopesHelper';
import FormInput from 'src/components/FormInput/FormInput';
import SavedReason from 'src/components/SavedReason/SavedReason';
import StackView, { SaveCancelFooter } from 'src/components/StackView/StackView';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';

import BulkAssignmentSelect from '../BulkAssignments/BulkAssignmentSelect';
import useBulkAssignments from '../BulkAssignments/useBulkAssignments';
import ReasonField, { optionalReasonRules, reasonRules } from '../ReasonField/ReasonField';
import { NewRoleFormProps, RoleOption, TextareaValue } from '../Users.types';

const formatContent = (content: string): string => {
  return content.replace(/, */g, ',\n');
};

const calculateHeight = (displayValue: string, defaultHeight: string): string => {
  const textarea = document.createElement('textarea');
  textarea.style.visibility = 'hidden';
  textarea.style.position = 'absolute';
  textarea.style.height = 'auto';
  textarea.style.whiteSpace = 'pre';
  textarea.value = displayValue;

  document.body.appendChild(textarea);
  const calculatedHeight = Math.max(textarea.scrollHeight, parseInt(defaultHeight));
  document.body.removeChild(textarea);

  return `${calculatedHeight}px`;
};

const fetcher = (input: RequestInfo): Promise<FetchRoles200> => {
  return AXIOS_INSTANCE({ url: input as string, method: 'GET' }).then((res) => res.data);
};

const getKey = (pageIndex: number, previousPageData: FetchRoles200) => {
  // reached the end
  if (previousPageData && !previousPageData.meta.next_page === null) {
    return null;
  }

  return `/roles?page=${pageIndex + 1}&per_page=100`; // SWR key
};

interface KeyValuePair {
  key: string;
  value: string;
}

const parseDataScopesInput = (input: string): KeyValuePair[] => {
  return input.split(',').map(pair => {
    const [key, value] = pair.split(':').map(s => s.trim());
    return { key, value };
  });
};

const NewRoleForm = ({ onClose, onSuccess, selectedUser, selectedRole, bulk = false, onAssignmentsChanged }: NewRoleFormProps) => {
  const {
    data: rolesData,
    error: rolesError,
    size,
    setSize
  } = useSWRInfinite<FetchRoles200>(getKey, fetcher, {
    revalidateFirstPage: false
  });
  const { data: userRolesData } = useFetchGrantedRolesToUser(selectedUser.id);
  const { error, setError } = useError();
  const bulkAssignments = useBulkAssignments();

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const { permissions, error: currentUserPermissionError } = useLocalPermissions();
  const companyAdminPermission = !currentUserPermissionError && permissions.companyAdminPermission;
  const manageLiteWebappUsersPermission =
    !currentUserPermissionError && permissions.manageLiteWebappUsersPermission;
  const canManageUserRoles = !currentUserPermissionError && permissions.manageUserRolesPermission;
  const isViewOnly = !!selectedRole && !canManageUserRoles;

  const initialDataScope = selectedRole ? formatDataScopesInput(selectedRole.data_scope) : '';
  const [dataPairs, setDataPairs] = useState<KeyValuePair[]>([]);
  const [dataScopeValue, setDataScopeValue] = useState<string>(initialDataScope);
  const [shouldRenderDataPairs, setShouldRenderDataPairs] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (rolesData) {
      // if there's still something to fetch, fetch it before setting select options
      if (rolesData.at(-1)?.meta.next_page !== null) {
        setSize(size + 1);
      } else {
        let roles = rolesData
          .reduce((acc: Role[], rolesPage) => [...acc, ...rolesPage.data.roles], [])
          .filter(
            ({ id, assignment_restricted }) =>
              selectedRole?.role_id === id ||
              (!assignment_restricted &&
                !userRolesData?.data.user_roles.find(({ role_id }) => role_id === id))
          );

        if (!companyAdminPermission) {
          roles = roles.filter(role => role.category !== 'company_app');
        }

        if (!manageLiteWebappUsersPermission) {
          roles = roles.filter(role => role.category !== 'lite_webapp');
        }

        const isCompanyUser = selectedUser.company_users && selectedUser.company_users.length > 0;
        if (!isCompanyUser) {
          roles = roles.filter(role => role.category !== 'company_app');
        }

        setRoleOptions(
          roles.map(({ id, name, category }) => ({
            value: id,
            label:
              category === 'lite_webapp'
                ? `${name} (Lite Webapps)`
                : category === 'company_app'
                  ? `${name} (Company App)`
                  : name
          }))
        );
      }
    }
  }, [
    rolesData,
    userRolesData,
    companyAdminPermission,
    manageLiteWebappUsersPermission,
    selectedRole,
    selectedUser.company_users
  ]);

  useEffect(() => {
    if (rolesError) {
      setError(rolesError);
    }
  }, [rolesError]);

  const handleTextareaChange = (value: string) => {
    setDataScopeValue(formatDataScopesInput(formatDataScopesData(value)));
    setDataPairs(parseDataScopesInput(value));
  };

  const handlePairChange = (index: number, keyOrValue: 'key' | 'value', newValue: string) => {
    const newData = dataPairs.map((pair, i) => {
      if (i === index) {
        return { ...pair, [keyOrValue]: newValue };
      }
      return pair;
    });
    setDataPairs(newData);
    setDataScopeValue(newData.map(pair => `${pair.key}:${pair.value}`).join(','));
  };

  const onSubmit = async (data: { roleId: RoleOption; reason: string; dataScopes: TextareaValue }) => {
    if (bulk && !selectedRole) {
      const shared = { reason: data.reason.trim(), data_scope: formatDataScopesData(dataScopeValue) };
      const complete = await bulkAssignments.submit((option) => grantRoleToUser(selectedUser.id, {
        user_role: { ...shared, role_id: option.value }
      }), onAssignmentsChanged);
      if (complete) onSuccess();
      return;
    }

    setIsSaving(true);
    if (selectedRole) {
      AXIOS_INSTANCE({
        url: `/users/${selectedUser.id}/roles/${selectedRole.role_id}`,
        method: 'PATCH',
        data: { user_role: {
          data_scope: formatDataScopesData(dataScopeValue),
          ...(data.reason.trim() ? { reason: data.reason.trim() } : {})
        } }
      }).then(onSuccess, (error: { response?: { data?: { message?: string } } }) => {
        setIsSaving(false);
        setError(error?.response?.data?.message ? (error.response.data as { message: string }) : DEFAULT_ERROR);
      });
    } else {
      grantRoleToUser(selectedUser.id, {
        user_role: userRoleData(data)
      }).then(onSuccess, (error) => {
        setIsSaving(false);
        setError(error?.response?.data?.message ? error?.response?.data : DEFAULT_ERROR);
      });
    }
  };

  const userRoleData = (data: { roleId: RoleOption; reason: string; dataScopes: TextareaValue }) => ({
    role_id: data.roleId.value,
    reason: data.reason.trim(),
    data_scope: formatDataScopesData(dataScopeValue)
  });

  const { control, handleSubmit } = useForm<{
    roleId: RoleOption;
    reason: string;
    dataScopes: TextareaValue;
  }>();

  const closeForm = () => {
    if (!bulkAssignments.isSaving) onClose();
  };

  const StackViewFooter = <SaveCancelFooter cancelLabel={isViewOnly ? 'Close' : 'Cancel'} hideSave={isViewOnly} isCancelDisabled={bulkAssignments.isSaving} isSaveDisabled={isSaving || (bulk && (bulkAssignments.isSaving || !bulkAssignments.selected.length))} onClose={closeForm} onSelect={handleSubmit(onSubmit)} />;

  const keyValuePattern = /\w+:\w+/;

  return (
    <StackView footer={StackViewFooter} onClose={closeForm} nested={true} title={isViewOnly ? 'View role' : selectedRole ? 'Edit role' : bulk ? 'Bulk add roles' : 'Grant new role'}>
      <FormInput label={bulk ? "Roles" : "Role name"}>
        {bulk && !selectedRole ? (
          <BulkAssignmentSelect options={userRolesData ? roleOptions : []} assignments={bulkAssignments} />
        ) : (
          <Controller
            name="roleId"
            control={control}
            render={({ field: { onChange } }) => (
              <Select
                onChange={onChange}
                options={roleOptions}
          isDisabled={!!selectedRole || isViewOnly}
                value={
                  selectedRole
                    ? roleOptions.find((option) => option.value === selectedRole.role_id)
                    : undefined
                }
              />
            )}
          />
        )}
      </FormInput>
      <Controller
        name="dataScopes"
        control={control}
        rules={selectedRole ? undefined : {
          validate: () => formatDataScopesData(dataScopeValue).length > 0 || 'Data scope is required.'
        }}
        render={({ field: { onChange }, fieldState }) => (
          <div>
            <label htmlFor="data" className="ds-text-body-base-semibold">Data Scopes</label>
            {shouldRenderDataPairs && dataPairs.map((pair, index) => (
              <div className="data-scope-block" key={index}>
                <input
                  name="data"
                  type="text"
                  disabled={isViewOnly}
                  value={pair.key}
                  onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                />
                <input
                  type="text"
                  disabled={isViewOnly}
                  value={pair.value}
                  onChange={(e) => handlePairChange(index, 'value', e.target.value)}
                />
              </div>
            ))}
            <Textarea
              name="data-scope"
              disabled={isViewOnly}
              height={calculateHeight(formatContent(dataScopeValue), "87")}
              onChange={(e) => {
                handleTextareaChange(e.value);
                onChange(e);
                if (keyValuePattern.test(e.value)) {
                  setShouldRenderDataPairs(true);
                } else {
                  setShouldRenderDataPairs(false);
                }
              }}
              value={formatContent(dataScopeValue)}
              error={fieldState.error?.message}
              placeholder="Data scopes must be separated by commas. i.e., company_id:1, company_id:2, country:ES"
            />
          </div>
        )}
      />
      {selectedRole && <SavedReason canReadReason={!currentUserPermissionError && permissions.readAssignmentDetailsPermission} loadReason={() => fetchUserRoleReason(selectedUser.id, selectedRole.role_id)} />}
      <Controller
        name="reason"
        control={control}
        defaultValue=""
        rules={selectedRole ? optionalReasonRules : reasonRules}
        render={({ field, fieldState }) => (
          <ReasonField
            value={field.value}
            onChange={field.onChange}
            inputRef={field.ref}
            error={fieldState.error}
            append={!!selectedRole}
          disabled={isViewOnly}
            placeholder={selectedRole ? "Write an additional reason to append" : "Write why this user needs this role"}
          />
        )}
      />
      {error}
    </StackView>
  );
};

export default NewRoleForm;
